import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import BandPreview from "./BandPreview";
import ReaderPreview from "./ReaderPreview";
import Interact from "./Interact";
import "./DesignStudio.css";
import "./DesignStudioOverlay.css";

const COLORS = [
  { name: "red",    hex: "#FF3B30", label: "Red" },
  { name: "orange", hex: "#FF9500", label: "Orange" },
  { name: "yellow", hex: "#FFD60A", label: "Yellow" },
  { name: "green",  hex: "#30D158", label: "Green" },
  { name: "cyan",   hex: "#64D2FF", label: "Cyan" },
  { name: "blue",   hex: "#0A84FF", label: "Blue" },
  { name: "purple", hex: "#BF5AF2", label: "Purple" },
  { name: "pink",   hex: "#FF4FC8", label: "Pink" },
  { name: "white",  hex: "#F2F2F7", label: "White" },
  { name: "off",    hex: "#1C1C1E", label: "Off" },
];

const MODE_DEFAULTS = {
  "1color": { outer: "cyan", inner: "cyan" },
  "2color": { outer: "blue", inner: "yellow" },
  "5color": { tl: "red", tr: "blue", c: "yellow", bl: "green", br: "purple" },
};

const ZONE_LABELS = {
  outer: "Outer Ring",
  inner: "Inner Circle",
  tl: "Top Left",
  tr: "Top Right",
  c: "Center",
  bl: "Bottom Left",
  br: "Bottom Right",
};

// Interact animation buttons (visual-only for now; band fires later)
const INTERACT_ACTIONS = [
  { key: "ropedrop", icon: "🌅", label: "ROPE DROP", cls: "ds-act-ropedrop" },
  { key: "dvc",      icon: "🏰", label: "DVC",       cls: "ds-act-dvc" },
  { key: "noentry",  icon: "🚫", label: "NO ENTRY",  cls: "ds-act-noentry" },
  { key: "alert",    icon: "🚨", label: "ALERT",     cls: "ds-act-alert" },
];

const SLIDE_DURATION = 300;
const LOADING_DURATION_MS = 4000;   // load phase; ring fill is synced to this
const TRANSMIT_DURATION = 18;        // countdown seconds
const READY_IDLE_MS = 30000;         // walk-away guard on the "ready" overlay

const DesignStudio = forwardRef(({ onTransmit, onPrepare, onCancel, device }, ref) => {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState(null);
  const [target, setTarget] = useState("outer");
  const [colors, setColors] = useState({
    outer: "blue", inner: "yellow",
    tl: "red", tr: "blue", c: "yellow", bl: "green", br: "purple",
  });
  const [slideClass, setSlideClass] = useState("");

  // Which view is showing in the right panel: "band" | "reader"
  const [stageView, setStageView] = useState("band");
  // Active interact animation (drives ReaderPreview); null = reader idle
  const [interactAnim, setInteractAnim] = useState(null);

  // "hidden" | "loading" | "ready" | "transmitting"
  const [overlayState, setOverlayState] = useState("hidden");
  const [countdown, setCountdown] = useState(TRANSMIT_DURATION);

  const loadingTimerRef = useRef(null);
  const tickTimerRef = useRef(null);
  const idleTimerRef = useRef(null); // 30s walk-away guard on the "ready" overlay

  useEffect(() => () => {
    clearTimeout(loadingTimerRef.current);
    clearTimeout(tickTimerRef.current);
    clearTimeout(idleTimerRef.current);
  }, []);

  // Countdown driver for the transmitting state
  useEffect(() => {
    if (overlayState !== "transmitting") return;
    if (countdown <= 0) {
      closeOverlay();
      return;
    }
    tickTimerRef.current = setTimeout(() => setCountdown((s) => s - 1), 1000);
    return () => clearTimeout(tickTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayState, countdown]);

  // Walk-away guard: the "ready" screen is reached via openOverlay → onPrepare(),
  // which starts the parent's WAKE_UP_FAST loop. If the user walks away here,
  // that loop would run forever. After 30s on "ready", tear down exactly like
  // Go Back: onCancel() stops the loop + sends cancel, then closeOverlay().
  // The effect cleanup clears the timer whenever we leave "ready" (Send →
  // transmitting, or Go Back → hidden), so no other handler needs to touch it.
  useEffect(() => {
    if (overlayState !== "ready") return;
    idleTimerRef.current = setTimeout(() => {
      if (onCancel) onCancel();
      closeOverlay();
    }, READY_IDLE_MS);
    return () => clearTimeout(idleTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayState]);

  const transitionTo = (newStep, dir) => {
    setSlideClass(dir === "forward" ? "exit-left" : "exit-right");
    setTimeout(() => {
      setStep(newStep);
      setSlideClass("");
    }, SLIDE_DURATION);
  };

  const handleModeClick = (m) => {
    setMode(m);
    setTarget(m === "5color" ? "tl" : "outer");
    setColors((prev) => ({ ...prev, ...MODE_DEFAULTS[m] }));
    setStageView("band");
    transitionTo(2, "forward");
  };

    const handleInteractClick = () => {
    setMode("interact");
  };

  // Exit from the Interact component: no slide animation (ds-slide isn't
  // mounted while Interact is up), just reset straight to step 1.
  const handleInteractExit = () => {
    setMode(null);
    setStageView("band");
    setInteractAnim(null);
    setSlideClass("");
    setStep(1);
  };

  const handleSwatchClick = (colorName) => {
    if (mode === "1color") {
      setColors((prev) => ({ ...prev, outer: colorName, inner: colorName }));
    } else {
      setColors((prev) => ({ ...prev, [target]: colorName }));
    }
  };
    // "Bands OFF" (TWO / THREE+ only): stays in this menu, but sends the same
  // proven single-color Off command as ONE → Off → Next. The design on screen
  // is left untouched.
  const allOffRef = useRef(false);
  const handleAllOff = () => {
    allOffRef.current = true;
    openOverlay();
  };
  const handleZoneTap = (zoneTarget) => setTarget(zoneTarget);

    const handleBack = () => {
    setStageView("band");
    setInteractAnim(null);
    transitionTo(1, "back");
    // Clear mode AFTER the slide-out, so step 2 doesn't flash the multi-color layout.
    setTimeout(() => setMode(null), SLIDE_DURATION);
  };
  // Interact action tap — visual only for now (band fires later)
  const handleInteractAction = (key) => {
    // Re-trigger even if same key by clearing first
    setInteractAnim(null);
    requestAnimationFrame(() => setInteractAnim(key));
  };

  /* ===== Overlay handlers (color modes) ===== */
  const openOverlay = () => {
    if (onPrepare) onPrepare(); // parent starts the WAKE_UP_FAST loop
    setOverlayState("loading");

    clearTimeout(loadingTimerRef.current);
    loadingTimerRef.current = setTimeout(() => {
      setOverlayState((s) => (s === "loading" ? "ready" : s));
    }, LOADING_DURATION_MS);
  };

  const handleSend = async () => {
  if (overlayState !== "ready") return;
  // Ask the parent FIRST. "sent" = we have control → run the countdown.
  // "pending" = take-control modal is up → hide, NO countdown; Yes →
  // parent calls beginTransmitCountdown(); No → back at selection.
  // "failed" = claim failed → back to selection, nothing sent.
    // Bands OFF → send single-color Off without changing the on-screen design.
  const sendArgs = allOffRef.current
    ? { mode: "1color", colors: { ...colors, outer: "off", inner: "off" } }
    : { mode, colors };
  const verdict = onTransmit ? await onTransmit(sendArgs) : "sent";
  if (verdict === "sent") {
    setCountdown(TRANSMIT_DURATION);
    setOverlayState("transmitting");
  } else {
    closeOverlay();
  }
};
  useImperativeHandle(ref, () => ({
  beginTransmitCountdown: () => {
    setCountdown(TRANSMIT_DURATION);
    setOverlayState("transmitting");
  },
}));

  const handleIlluminated = () => {
    if (onCancel) onCancel();
    closeOverlay();
  };

  const closeOverlay = () => {
    clearTimeout(loadingTimerRef.current);
    clearTimeout(tickTimerRef.current);
    clearTimeout(idleTimerRef.current);
    setOverlayState("hidden");
        allOffRef.current = false; // next Next sends the real design again
  };

  const handleOverlayBack = () => {
    if (onCancel) onCancel();
    closeOverlay();
  };

  /* ===== Renderers ===== */
  const renderStep1 = () => (
    <>
      <div className="ds-step-hero">
        <div className="ds-step-tag-row">
          <span className="ds-step-tag">STEP 1</span>
          <div className="ds-step-pills">
            <span className="ds-step-pill active" />
            <span className="ds-step-pill" />
          </div>
        </div>
        <h1 className="ds-step-title">How Many Colors?</h1>
      </div>
      <div className="ds-mode-buttons">
        <button className="ds-mode-btn" onClick={() => handleModeClick("1color")}>ONE</button>
        <button className="ds-mode-btn" onClick={() => handleModeClick("2color")}>TWO</button>
        <button className="ds-mode-btn" onClick={() => handleModeClick("5color")}>THREE+</button>
        <button className="ds-mode-btn ds-mode-btn-interact" onClick={handleInteractClick}>
          <span className="ds-interact-label">INTERACT</span>
        </button>
      </div>
    </>
  );

  const renderInteractStep = () => (
    <>
      <div className="ds-step-hero">
        <div className="ds-step-tag-row">
          <span className="ds-step-tag">INTERACT</span>
          <div className="ds-step-pills">
            <span className="ds-step-pill active" />
            <span className="ds-step-pill active" />
          </div>
        </div>
        <h1 className="ds-step-title">Pick an Animation</h1>
      </div>

      <div className="ds-interact-buttons">
        {INTERACT_ACTIONS.map((a) => (
          <button
            key={a.key}
            className={`ds-interact-action ${a.cls} ${interactAnim === a.key ? "active" : ""}`}
            onClick={() => handleInteractAction(a.key)}
          >
            <span className="ds-act-icon">{a.icon}</span>
            <span className="ds-act-text">{a.label}</span>
          </button>
        ))}
      </div>

      <div className="ds-actions">
        <button className="ds-btn-big ds-btn-back" onClick={handleBack}>← Back</button>
      </div>
    </>
  );

  const renderStep2 = () => {
    if (mode === "interact") return renderInteractStep();

    const title = mode === "1color" ? "Pick Your Color" : "Pick Your Colors";
    const currentColor = colors[target] || colors.outer;

    return (
      <>
        <div className="ds-step-hero">
          <div className="ds-step-tag-row">
            <span className="ds-step-tag">STEP 2</span>
            <div className="ds-step-pills">
              <span className="ds-step-pill active" />
              <span className="ds-step-pill active" />
            </div>
          </div>
          <h1 className="ds-step-title">{title}</h1>
        </div>

        {mode !== "1color" && (
          <>
            <p className="ds-instruction">Tap a zone on the band, then choose a color.</p>
            <div className="ds-current-selection">
              <div className="ds-cs-label">CURRENT SELECTION</div>
              <div className="ds-cs-value">{ZONE_LABELS[target]}</div>
            </div>
          </>
        )}

        <div className="ds-swatch-grid">
          {COLORS.map((c) => (
            <div
              key={c.name}
              className={`ds-swatch ${c.name === "off" ? "off" : ""} ${currentColor === c.name ? "selected" : ""}`}
              style={{ background: c.hex }}
              onClick={() => handleSwatchClick(c.name)}
              title={c.label}
            />
          ))}
        </div>

                {mode !== "1color" && (
          <button
            onClick={handleAllOff}
            style={{ marginTop: 16, marginBottom: 18, width: "100%", height: 50, borderRadius: 14, border: "none", background: "#4B5563", color: "#FFFFFF", fontFamily: "Lato, sans-serif", fontSize: 19, fontWeight: 900, letterSpacing: 1, cursor: "pointer" }}          >
            Bands OFF
          </button>
        )}

        <div className="ds-actions">
          <button className="ds-btn-big ds-btn-back" onClick={handleBack}>Back</button>
          <button className="ds-btn-big ds-btn-transmit" onClick={openOverlay}>Next</button>
        </div>
      </>
    );
  };

  /* ===== Band-panel overlay (color modes only) ===== */
  const renderOverlay = () => {
    if (overlayState === "hidden") return null;

    if (overlayState === "loading") {
      return (
        <div className="dso-band-overlay dso-loading">
          <div className="dso-load-wrap">
            <svg className="dso-load-ring" viewBox="0 0 280 280">
              <circle className="dso-load-ring-track" cx="140" cy="140" r="130" />
              <circle
                className="dso-load-ring-fill"
                cx="140" cy="140" r="130"
                style={{ animationDuration: `${LOADING_DURATION_MS}ms` }}
              />
            </svg>
            <div className="dso-load-text">
              Loading Your<br />Design
              <span className="dso-dots"><span>.</span><span>.</span><span>.</span></span>
            </div>
          </div>
        </div>
      );
    }

    if (overlayState === "ready") {
      return (
        <div className="dso-band-overlay dso-ready">
          <div className="dso-ready-content">
            <h2 className="dso-big-heading">Ready To Send<br />The Magic?</h2>
            <button className="dso-launch-btn" onClick={handleSend}>Send Now!</button>
            <button className="dso-back-pill" onClick={handleOverlayBack}>Go Back</button>
          </div>
        </div>
      );
    }

    // transmitting
    return (
      <div className="dso-band-overlay dso-transmitting">
        <div className="dso-transmit-content">
          <div className="dso-rings">
            <span /><span /><span />
            <div className="dso-timer">{countdown}</div>
          </div>
          <h2 className="dso-heading">Beacon transmitting...</h2>
          <p className="dso-transmit-body">May take up to 20 seconds for all bands to respond</p>
          <button className="dso-illuminated-btn" onClick={handleIlluminated}>
            Already illuminated?
            <span className="dso-illum-sub">Tap to pick another color!</span>
          </button>
        </div>
      </div>
    );
  };

  if (mode === "interact") return <Interact onBack={handleInteractExit} device={device} />;
  return (
    <div className="ds-designer">
      <div className={`ds-control-panel ${overlayState === "transmitting" ? "dso-left-locked" : ""}`}>
        <div className={`ds-slide ${slideClass}`}>
          {step === 1 ? renderStep1() : renderStep2()}
        </div>
      </div>
      <div className="ds-band-stage">
        {stageView === "reader" ? (
          <ReaderPreview animation={interactAnim} />
        ) : (
          <BandPreview mode={mode} colors={colors} onZoneTap={handleZoneTap} />
        )}
        {stageView === "band" && renderOverlay()}
      </div>
    </div>
  );
});

export default DesignStudio;
